import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable, of } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { Integration } from '../../../core/models/integrations';
import { IntegrationService } from '../../../core/services/integration.service';
import { User } from 'src/app/core/models/user';
import {
  formatIngestorApiError,
  IngestorService,
} from '../../admin/organizations/components/integrations/services/ingestor.service';
import { ConnectWiseService } from '../../../core/services/connectwise.service';
import { entraidLogs, MappedCompany, stellarLogs, tenantMappings } from './sample-data/data';
// Kendo UI imports
import { ButtonModule, DropDownButtonModule } from '@progress/kendo-angular-buttons';
import { DialogCloseResult, DialogModule, DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { GridModule } from '@progress/kendo-angular-grid';
import { IconsModule } from '@progress/kendo-angular-icons';
import { CheckBoxModule, InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule, TabStripModule, TileLayoutModule } from '@progress/kendo-angular-layout';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import {
  arrowLeftIcon,
  checkOutlineIcon,
  clockIcon,
  gearIcon,
  hyperlinkOpenIcon,
  lockIcon,
  saveIcon,
  SVGIcon,
  trackChangesIcon,
  userIcon,
} from '@progress/kendo-svg-icons';

// Custom components
import { ListCardComponent } from 'src/app/core/components/list-card/list-card.component';
import { DynamicFormBuilder } from 'src/app/core/services/dynamic-form-builder.service';
import {
  DynamicFormComponent,
  TestConnectionHandler,
} from '../../../core/components/dynamic-form/dynamic-form.component';
import { ConnectWiseFormComponent } from './components/forms/connectwise/connectwise-form.component';
import { ConfiguredIngestionFormComponent } from './components/forms/configured-ingestion-form/configured-ingestion-form.component';
import { ServiceNowFormComponent } from './components/forms/servicenow/servicenow-form.component';
import { TenantMappingComponent } from './components/tenant-mapping/tenant-mapping.component';
import { IngestorTenantMappingComponent } from './components/ingestor-tenant-mapping/ingestor-tenant-mapping.component';

/**
 * Configuration option for integration settings dropdown
 */
interface ConfigOption {
  text: string;
  icon?: string;
}

/**
 * Deep-link aliases for the `?integration=` query param. Maps short URL-safe
 * keys to the integration's `name` field on the API response. Allows other
 * pages (e.g. the Identity Security onboarding empty state) to link straight
 * to a specific integration card. Case-insensitive name match at lookup time.
 *
 * Note: Microsoft 365 may not exist in the integrations list yet; the alias is
 * declared in advance so the deep link works the moment it ships.
 */
const INTEGRATION_DEEPLINK_ALIASES: Record<string, string> = {
  entraid: 'Microsoft EntraID',
  m365: 'Microsoft 365',
};

/**
 * Types of integrations available in the system
 */
enum IntegrationType {
  DataIngestion = 'Data Ingestion',
  Ticketing = 'Ticketing',
}

/**
 * Filter options for integration list
 */
type FilterType = 'all' | 'data-ingestion' | 'ticketing';

/**
 * Tab options for integration details
 */
type TabType = 'logs' | 'mappings' | 'configuration';

/** Which Mappings screen an integration opens: ticketing keeps the ConnectWise one. */
export type MappingKind = 'ticketing' | 'ingestion';

export function categoryNameOf(integration: Pick<Integration, 'category'>): string {
  if (typeof integration.category === 'number') {
    return integration.category === 1 ? IntegrationType.DataIngestion : IntegrationType.Ticketing;
  }
  return integration.category.name;
}

export function resolveMappingKind(integration: Integration | undefined): MappingKind | null {
  if (!integration?.can_be_multitenant) {
    return null;
  }
  switch (categoryNameOf(integration)) {
    case IntegrationType.Ticketing:
      return 'ticketing';
    case IntegrationType.DataIngestion:
      return 'ingestion';
    default:
      return null;
  }
}

/**
 * Component for managing third-party integrations
 * Handles configuration, testing, and monitoring of various integrations
 * like Microsoft EntraID, ConnectWise, ServiceNow, etc.
 */
@Component({
  selector: 'app-integrations',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    IconsModule,
    LayoutModule,
    InputsModule,
    LabelModule,
    GridModule,
    CheckBoxModule,
    TileLayoutModule,
    NotificationModule,
    TabStripModule,
    DropDownButtonModule,
    TenantMappingComponent,
    IngestorTenantMappingComponent,
    ListCardComponent,
    DynamicFormComponent,
    ConnectWiseFormComponent,
    ServiceNowFormComponent,
    ConfiguredIngestionFormComponent,
  ],
  templateUrl: './integrations.component.html',
  styleUrl: './integrations.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntegrationsComponent implements OnInit {
  /** Form for integration configuration */
  configForm!: FormGroup;
  /** Loading state for async operations */
  loading = signal(false);
  /** Currently selected filter for integration list */
  selectedFilter: FilterType = 'all';
  /** Currently selected tab in integration details */
  selectedTab: TabType = 'logs';
  /** Mappings screen open in the details pane, or null when closed */
  mappingKind = signal<MappingKind | null>(null);
  /** Signal indicating if connection test was successful */
  testSuccess = signal<boolean>(false);

  /** SVG Icons used in the component */
  public gearIcon: SVGIcon = gearIcon;
  public checkOutlineIcon: SVGIcon = checkOutlineIcon;
  public saveIcon: SVGIcon = saveIcon;
  public lockIcon: SVGIcon = lockIcon;
  public hyperlinkOpenIcon: SVGIcon = hyperlinkOpenIcon;
  public clockIcon: SVGIcon = clockIcon;
  public arrowLeftIcon: SVGIcon = arrowLeftIcon;
  public trackChangesIcon: SVGIcon = trackChangesIcon;
  public userIcon: SVGIcon = userIcon;

  /** Configuration options for selected integration */
  configOptions: ConfigOption[] = [];

  /** Sample data for EntraID logs */
  entraidLogs = entraidLogs;
  /** Sample data for Stellar Cyber logs */
  stellarLogs = stellarLogs;
  /** Sample data for tenant mappings */
  tenantMappings = tenantMappings;

  /** Signal containing the list of available integrations */
  integrationsList = signal<Integration[]>([]);
  /** Signal containing the currently selected integration ID */
  integrationId = signal<number | null>(null);
  selectedIntegration = computed(() => this.integrationsList().find((int) => int.id === this.integrationId()));
  /** Signal containing the fetched integration details (passed to child to avoid redundant API calls) */
  integrationDetails = signal<any>(null);

  /** Test connection handler for data ingestor integrations */
  ingestorTestHandler: TestConnectionHandler = (orgId, formId, formValues) => {
    const credentials: any = { ...formValues };
    for (const key of ['apiUrl', 'authUrl', 'tenantId', 'clientId', 'clientSecret', 'apiKey']) {
      if (credentials[key]) credentials[key] = { value: credentials[key] };
    }
    return this.ingestorService.testConnection({
      ingestor_integration: formId,
      organization: orgId,
      integration_credentials: credentials,
    });
  };

  /** Test connection handler for ticketing integrations */
  ticketingTestHandler: TestConnectionHandler = (orgId, formId, formValues) => {
    return this.connectWiseService.testConnectionGeneric(orgId, formId, formValues);
  };

  private integrationService = inject(IntegrationService);
  private ingestorService = inject(IngestorService);
  private connectWiseService = inject(ConnectWiseService);
  /** Angular router for accessing route parameters */
  private route = inject(ActivatedRoute);
  /** DestroyRef for takeUntilDestroyed */
  private destroyRef = inject(DestroyRef);
  private readonly user = inject(User);

  /** Signal containing the current organization ID */
  organizationId = signal<number | null>(null);

  /**
   * Pending deep-link target from `?integration=` query param. Consumed once on
   * the first integration list load, then cleared so subsequent reloads (e.g.
   * after save) don't reselect.
   */
  private pendingDeeplinkAlias: string | null = null;

  /**
   * Component constructor
   * @param fb Angular FormBuilder for creating reactive forms
   * @param cdr ChangeDetectorRef for manual change detection
   * @param notificationService Service for showing notifications
   * @param dynamicFormBuilder Service for building dynamic forms
   */
  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    private dynamicFormBuilder: DynamicFormBuilder,
    private dialogService: DialogService
  ) {
    this.initOrganizationId();
  }

  /**
   * Angular lifecycle hook - initialize component
   * Sets up the configuration form and loads available integrations
   */
  ngOnInit(): void {
    this.configForm = this.fb.group({});
  }

  /**
   * Initialize organization ID from route query parameters
   * @private
   */
  private initOrganizationId(): void {
    // Watch for organizationId changes and reload integrations
    effect(
      () => {
        const orgId = this.organizationId();
        if (orgId) {
          this.loadIntegrations();
        }
      },
      { allowSignalWrites: true }
    );

    this.route.queryParams
      .pipe(
        filter((params) => params['organizationId']),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((params) => {
        // Capture the deep-link alias (if any) before triggering the org effect.
        // We store it once; loadIntegrations() consumes it on the next fetch.
        const aliasParam = params['integration'];
        if (typeof aliasParam === 'string' && aliasParam.length > 0) {
          this.pendingDeeplinkAlias = aliasParam;
        }
        this.organizationId.set(+params['organizationId']);
      });
  }

  /**
   * Load available integrations from the API
   * @private
   */
  private loadIntegrations(integration_id?: number): void {
    const orgId = this.organizationId();

    this.integrationService
      .getIntegrationsByOrganization(orgId!)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const mappedIntegrations = response.value.map((integration) => ({
            ...integration,
            // Handle configuration_status as either string or object
            status:
              typeof integration.configuration_status === 'string'
                ? integration.configuration_status
                : (integration.configuration_status as any)?.name || 'Not Configured',
            // Handle category as either object or number
            category:
              typeof integration.category === 'number'
                ? { id: integration.category, name: integration.category === 1 ? 'Data Ingestion' : 'Ticketing' }
                : integration.category,
          }));

          this.integrationsList.set(mappedIntegrations);
          if (mappedIntegrations.length > 0) {
            // Deep-link via `?integration=<alias>` takes precedence on first load.
            const deepLinkMatch = this.resolveDeeplinkIntegration(mappedIntegrations);

            if (integration_id) {
              this.selectIntegration(integration_id);
            } else if (deepLinkMatch) {
              this.selectIntegration(deepLinkMatch.id);
            } else {
              // Find the first integration visible to the current user
              const firstVisible = mappedIntegrations.find(
                (int) => this.isCyflareUser() || int.allow_ui_modification !== false
              );
              if (firstVisible) {
                this.selectIntegration(firstVisible.id);
              }
            }
          }
        },
        error: (error) => {
          console.error('Error loading integrations:', error);
          this.showNotification('Failed to load integrations', 'error');
        },
      });
  }

  /**
   * Get category name from integration
   * @param integration The integration object
   * @returns The category name string
   */
  getCategoryName(integration: Integration): string {
    return categoryNameOf(integration);
  }

  /**
   * Resolve the pending `?integration=<alias>` deep-link to an actual integration in
   * the loaded list. Consumes (clears) the alias so reloads after save don't
   * reselect. Returns null when no alias is pending or no match is found.
   */
  private resolveDeeplinkIntegration(integrations: Integration[]): Integration | null {
    const alias = this.pendingDeeplinkAlias;
    if (!alias) return null;
    this.pendingDeeplinkAlias = null;

    const targetName = INTEGRATION_DEEPLINK_ALIASES[alias.toLowerCase()];
    if (!targetName) return null;

    const targetLower = targetName.toLowerCase();
    return integrations.find((int) => (int.name ?? '').toLowerCase() === targetLower) ?? null;
  }

  /**
   * Select an integration and initialize its configuration form
   * @param integrationId The ID of the integration to select
   */
  selectIntegration(integrationId: number): void {
    this.integrationId.set(integrationId);
    this.mappingKind.set(null);
    this.testSuccess.set(false);

    const integration = this.integrationsList().find((int) => int.id === integrationId);
    const integrationName = integration?.name || '';

    // Fetch the full integration details to get accurate can_be_multitenant value
    this.integrationService
      .getFormSetup(integrationId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (formSetup) => {
          // Update the integration in the list with the correct can_be_multitenant value
          const currentList = this.integrationsList();
          const updatedList = currentList.map((int) =>
            int.id === integrationId ? { ...int, can_be_multitenant: formSetup.can_be_multitenant } : int
          );
          this.integrationsList.set(updatedList);

          this.integrationService.setCanBeMultitenant(formSetup.can_be_multitenant);
          this.updateConfigOptions(integrationId.toString());
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error fetching integration form setup:', error);
          // Fallback to the integration list value
          this.integrationService.setCanBeMultitenant(integration?.can_be_multitenant || false);
          this.updateConfigOptions(integrationId.toString());
          this.cdr.markForCheck();
        },
      });

    if (
      integrationName === 'Microsoft EntraID' ||
      integrationName === 'Sentinel One' ||
      integrationName === 'Microsoft Defender'
    ) {
      this.dynamicFormBuilder
        .createFormForIntegration(integrationId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          this.configForm = result.formGroup;
          this.cdr.markForCheck();
        });
    }

    // For ticketing integrations, fetch integration details to get the pk
    // so we can distinguish POST vs PUT, have it ready for DELETE, and pass to child
    if (integration && this.getCategoryName(integration) === 'Ticketing') {
      this.connectWiseService
        .getIntegrationDetails(this.organizationId()!, this.integrationId())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (details) => {
            this.integrationDetails.set(details);
            if (details?.pk) {
              this.onDetailPkLoaded(details.pk);
            } else if (details?.integration === null) {
              // No real integration detail exists — ensure status is "Not Configured"
              const list = this.integrationsList();
              const updated = list.map((int) =>
                int.id === integrationId ? { ...int, status: 'Not Configured', pk: undefined } : int
              );
              this.integrationsList.set(updated);
              this.cdr.markForCheck();
            }
          },
          error: () => {
            this.integrationDetails.set(null);
            // No details found — integration has no detail record (not configured yet)
          },
        });
    }

    this.testSuccess.set(false);
    this.cdr.markForCheck();
  }

  /**
   * Update configuration options based on selected integration
   * @param integrationId The ID of the integration
   */
  updateConfigOptions(integrationId: string): void {
    const options: ConfigOption[] = [];

    if (this.supportsMultiTenant(integrationId)) {
      options.push({ text: 'Mappings', icon: 'hyperlink-open' });
    }

    options.push({ text: 'Delete Configuration', icon: 'trash' });

    this.configOptions = options;
  }

  /**
   * Handle configuration option selection from dropdown
   * @param event The selection event from the dropdown
   */
  onConfigOptionSelect(event: any): void {
    let selectedOption = '';
    console.log('clicked');
    if (event) {
      if (event.text) {
        selectedOption = event.text;
      } else if (event.item && event.item.text) {
        selectedOption = event.item.text;
      }
    }

    if (!selectedOption) {
      console.error('Could not determine selected option in onConfigOptionSelect:', event);
      return;
    }

    console.log('Selected option:', selectedOption);

    if (selectedOption === 'Mappings') {
      this.openTenantMapping();
    } else if (selectedOption === 'Delete Configuration') {
      console.log('Calling deleteConfiguration');
      this.deleteConfiguration();
    }
    this.cdr.markForCheck();
  }

  /**
   * Open the tenant mapping dialog
   */
  openTenantMapping(): void {
    this.mappingKind.set(resolveMappingKind(this.selectedIntegration()));
    this.cdr.markForCheck();
  }

  /**
   * Close the tenant mapping dialog
   */
  closeTenantMapping(): void {
    this.mappingKind.set(null);
    this.cdr.markForCheck();
  }

  /**
   * Handle saving of tenant mappings
   * @param mappings Array of tenant mappings to save
   */
  handleSaveMappings(mappings: MappedCompany[]): void {
    // TODO: Implement actual mapping save functionality
    // this.tenantMappings = mappings.map((mapping) => ({
    //   internalTenantId: mapping.internalId,
    //   internalTenantName: mapping.internalName,
    //   externalTenantId: mapping.externalId,
    //   externalTenantName: mapping.externalName,
    // }));

    this.mappingKind.set(null);
    this.cdr.markForCheck();
  }

  /**
   * Handle tab selection in integration details
   * @param e Tab selection event
   */
  onTabSelect(e: any): void {
    if (e.index === 0) {
      this.selectedTab = 'logs';
    } else if (e.index === 1) {
      this.selectedTab = 'mappings';
    } else {
      this.selectedTab = 'configuration';
    }
    this.cdr.markForCheck();
  }

  /**
   * Filter integrations by type
   * @param filter The filter type to apply
   */
  filterIntegrations(filter: FilterType): void {
    this.selectedFilter = filter;
    this.cdr.markForCheck();
  }

  /**
   * Determine if an integration should be shown based on current filter
   * @param id The integration ID to check
   * @returns True if the integration should be displayed
   */
  showIntegration(id: number): boolean {
    const integration = this.integrationsList().find((int) => int.id === id);
    if (!integration) {
      return false;
    }

    // Hide integrations with allow_ui_modification=false for non-Cyflare users
    if (!this.isCyflareUser() && integration.allow_ui_modification === false) {
      return false;
    }

    if (this.selectedFilter === 'all') {
      return true;
    }

    if (this.selectedFilter === 'data-ingestion') {
      return typeof integration.category === 'number'
        ? integration.category === 1
        : integration.category.name === 'Data Ingestion';
    }

    if (this.selectedFilter === 'ticketing') {
      return typeof integration.category === 'number'
        ? integration.category === 2
        : integration.category.name === 'Ticketing';
    }

    return true;
  }

  /**
   * Check if the current user is a Cyflare internal user
   * @private
   */
  isCyflareUser(): boolean {
    return this.user.isCyflareUser();
  }

  /**
   * Reset and configure the form based on integration type
   * @param integrationId The integration ID to configure for
   */
  resetConfigForm(integrationId: number): void {
    Object.keys(this.configForm.controls).forEach((key) => {
      this.configForm.get(key)?.clearValidators();
      this.configForm.get(key)?.updateValueAndValidity();
    });

    this.testSuccess.set(false);

    const integration = this.integrationsList().find((int) => int.id === integrationId);
    const integrationName = integration?.name || '';
    const supportsMultiTenant = integration ? this.supportsMultiTenant(integrationId.toString()) : false;

    if (integrationName === 'Microsoft EntraID' || integrationName === 'Microsoft Defender') {
      // Defender shares EntraID's OAuth client-credentials field set
      // (tenantId/clientId/clientSecret); apiUrl/authUrl are rebuilt from the
      // backend reactive_form on re-select.
      this.configForm.reset({
        clientId: '',
        clientSecret: '',
        tenantId: '',
        supportsMultiTenant: false,
      });

      this.configForm.get('clientId')?.setValidators(Validators.required);
      this.configForm.get('clientSecret')?.setValidators(Validators.required);
      this.configForm.get('tenantId')?.setValidators(Validators.required);
    } else if (integrationName === 'ConnectWise') {
      this.configForm.reset({
        apiKey: '',
        apiUrl: '',
        supportsMultiTenant: true,
      });

      this.configForm.get('apiKey')?.setValidators(Validators.required);
      this.configForm.get('apiUrl')?.setValidators(Validators.required);
    } else if (integrationName === 'Sentinel One') {
      this.configForm.reset({
        apiKey: '',
        apiUrl: '',
        supportsMultiTenant: true,
      });

      this.configForm.get('apiKey')?.setValidators(Validators.required);
      this.configForm.get('apiUrl')?.setValidators(Validators.required);
    } else if (integrationName === 'Stellar Cyber') {
      this.configForm.reset({
        companyId: '',
        apiKey: '',
        apiUrl: '',
        supportsMultiTenant: true,
      });

      this.configForm.get('companyId')?.setValidators(Validators.required);
      this.configForm.get('apiKey')?.setValidators(Validators.required);
      this.configForm.get('apiUrl')?.setValidators(Validators.required);
    } else if (integrationName === 'ServiceNow PSA') {
      this.configForm.reset({
        username: '',
        password: '',
        instanceUrl: '',
        supportsMultiTenant: true,
      });

      this.configForm.get('username')?.setValidators(Validators.required);
      this.configForm.get('password')?.setValidators(Validators.required);
      this.configForm.get('instanceUrl')?.setValidators(Validators.required);
    } else {
      this.configForm.reset({
        supportsMultiTenant: supportsMultiTenant,
      });
    }

    Object.keys(this.configForm.controls).forEach((key) => {
      this.configForm.get(key)?.updateValueAndValidity();
    });

    this.cdr.markForCheck();
  }

  /**
   * Trigger immediate synchronization for the selected integration
   */
  syncNow(): void {
    this.loading.set(true);
    this.cdr.markForCheck();

    // Simulate API call
    setTimeout(() => {
      this.loading.set(false);

      const integration = this.integrationsList().find((int) => int.id === this.integrationId());
      const integrationName = integration?.name || '';

      if (integrationName === 'Microsoft EntraID') {
        this.entraidLogs.unshift({
          timestamp: new Date().toLocaleString(),
          status: 'active',
          message: 'Sync completed successfully',
        });
      } else if (integrationName === 'Stellar Cyber') {
        this.stellarLogs.unshift({
          timestamp: new Date().toLocaleString(),
          status: 'active',
          message: 'Sync completed successfully',
        });
      }

      this.showNotification('Sync completed successfully', 'success');
      this.cdr.markForCheck();
    }, 1500);
  }

  /**
   * Test the connection with the current integration configuration
   * Note: This is only used for static forms (Ticketing). Dynamic forms handle their own test connection.
   */
  testConnection(success?: boolean): void {
    const isSuccessful = success !== undefined ? success : false;
    const currentForm = this.getCurrentForm();

    if (!isSuccessful) {
      currentForm.markAllAsTouched();
      return;
    }
    this.testSuccess.set(true);
    this.cdr.markForCheck();
    this.loading.set(false);
  }

  /**
   * Save the current integration configuration (for static forms like ServiceNow)
   */
  saveConfiguration(): void {
    const currentForm = this.getCurrentForm();

    if (!this.testSuccess()) {
      this.showNotification('Please test the connection before saving', 'warning');
      return;
    }

    this.loading.set(true);
    this.cdr.markForCheck();

    const formValues = currentForm.value;

    const formConfig: { controls: { [key: string]: any } } = {
      controls: {},
    };

    Object.keys(formValues).forEach((key) => {
      formConfig.controls[key] = {
        value: formValues[key],
        validators: currentForm.get(key)?.validator ? { required: true } : {},
      };
    });

    this.dynamicFormBuilder
      .saveFormConfig(this.integrationId() as number, formConfig)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loading.set(false);
        this.showNotification('Configuration saved successfully', 'success');
        this.loadIntegrations();
        this.cdr.markForCheck();
      });
  }

  /**
   * Save handler for data ingestor integrations from dynamic form.
   * Receives raw form values, builds the ingestor save request.
   */
  saveIngestorConfig(formValues: any): void {
    this.loading.set(true);
    this.cdr.markForCheck();

    const credentials: any = { ...formValues };
    for (const key of ['apiUrl', 'authUrl', 'tenantId', 'clientId', 'clientSecret', 'apiKey']) {
      if (credentials[key]) credentials[key] = { value: credentials[key] };
    }

    const integration = this.selectedIntegration();
    const createRequest = {
      ingestor_integration: this.integrationId()!,
      organization: this.organizationId()!,
      configuration_status: 2,
      integration_credentials: credentials,
      is_multitenant: integration?.can_be_multitenant === true,
    };

    // Update in place: deleting the record would cascade to its tenant mappings.
    const save$ = this.isConfigured(integration?.status)
      ? this.ingestorService.getIngestorRecordsByOrganizationv2(this.organizationId()!, this.integrationId()!).pipe(
          switchMap((record) =>
            record?.id
              ? this.ingestorService.updateIngestorRecord(record.id, {
                  integration_credentials: credentials,
                  configuration_status: 2,
                })
              : this.ingestorService.createIngestorRecord(createRequest)
          )
        )
      : this.ingestorService.createIngestorRecord(createRequest);

    this.handleSaveResult(save$);
  }

  /**
   * Save handler for ticketing integrations from dynamic form.
   * Receives raw form values, calls the generic ticketing save/update endpoint.
   */
  saveTicketingConfig(formValues: any): void {
    this.loading.set(true);
    this.cdr.markForCheck();

    const integration = this.integrationsList().find((int) => int.id === this.integrationId());
    const alreadyConfigured = this.isConfigured(integration?.status);

    const save$ = alreadyConfigured
      ? this.connectWiseService.updateIntegrationDetailsGeneric(
          this.organizationId()!,
          this.integrationId(),
          formValues
        )
      : this.connectWiseService.saveIntegrationDetailsGeneric(this.organizationId()!, this.integrationId(), formValues);

    this.handleSaveResult(save$);
  }

  /**
   * Shared save result handler for dynamic form save operations.
   */
  private handleSaveResult(save$: Observable<any>): void {
    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.loading.set(false);
        this.showNotification('Configuration saved successfully', 'success');
        const currentId = this.integrationId()!;
        this.integrationId.set(null);
        this.loadIntegrations(currentId);
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.loading.set(false);
        const errorMessage = formatIngestorApiError(error, 'Failed to save configuration');
        this.showNotification('Error saving configuration: ' + errorMessage, 'error');
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Get CSS class name based on integration status
   * @param status The integration status
   * @returns CSS class name for styling
   */
  getStatusClass(status: string): string {
    switch (status) {
      case 'Configured':
      case 'Connection Configured':
      case 'Configuration Configured':
        return 'status-active';
      case 'Warning':
        return 'status-warning';
      case 'Error':
        return 'status-error';
      case 'Not Configured':
      default:
        return 'status-disconnected';
    }
  }

  /**
   * Check if a status value represents a configured state
   * @param status The integration status string
   * @returns True if the integration is in any configured state
   */
  isConfigured(status: string | undefined): boolean {
    return status === 'Configured' || status === 'Connection Configured' || status === 'Configuration Configured';
  }

  /**
   * Format date string to human-readable relative time
   * @param dateString Optional date string to format
   * @returns Formatted date string (e.g., "2 hours ago", "Just now")
   */
  formatDate(dateString?: string): string {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    // Less than a minute
    if (diff < 60000) {
      return 'Just now';
    }

    // Less than an hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    }

    // Less than a day
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }

    // Format as date
    return date.toLocaleDateString();
  }

  /**
   * Check if an integration supports multi-tenant configuration
   * @param integrationId The integration ID to check
   * @returns True if the integration supports multi-tenant setup
   */
  supportsMultiTenant(integrationId: string): boolean {
    const integration = this.integrationsList().find((int) => int.id === parseInt(integrationId));
    return integration?.can_be_multitenant ?? false;
  }

  /**
   * Get the current configuration form
   * @private
   * @returns The current FormGroup instance
   */
  private getCurrentForm(): FormGroup {
    return this.configForm;
  }
  /**
   * Display a notification to the user
   * @param message The message to display
   * @param type The type of notification (info, success, warning, error)
   */
  showNotification(message: string, type: 'info' | 'success' | 'warning' | 'error'): void {
    this.notificationService.show({
      content: message,
      cssClass: 'notification-' + type,
      animation: { type: 'slide', duration: 400 },
      hideAfter: 3000,
      position: { horizontal: 'right', vertical: 'top' },
      type: { style: type, icon: true },
    });
  }

  /**
   * Handle configurationSaved event from child form components.
   * Refreshes the integration list and re-selects the current integration.
   */
  onConfigurationSaved(): void {
    const currentId = this.integrationId()!;
    this.integrationId.set(null);
    this.integrationDetails.set(null);
    this.loadIntegrations(currentId);
  }

  /**
   * Store the integration detail pk when loaded by a child form component.
   * This pk is needed for DELETE and for deciding POST vs PUT.
   */
  onDetailPkLoaded(pk: number): void {
    const currentId = this.integrationId();
    if (!currentId) return;

    const list = this.integrationsList();
    const updated = list.map((int) => (int.id === currentId ? { ...int, pk } : int));
    this.integrationsList.set(updated);
  }

  /**
   * Delete the configuration for the selected integration
   */
  deleteConfiguration(): void {
    console.log('deleteConfiguration called');
    const integrationId = this.integrationId();
    const orgId = this.organizationId();

    if (!integrationId || !orgId) {
      this.showNotification('No integration selected', 'error');
      return;
    }

    const integration = this.integrationsList().find((int) => int.id === integrationId);
    const integrationName = integration?.name || 'this integration';
    const isTicketingIntegration = this.getCategoryName(integration!) === 'Ticketing';
    const integrationInstanceId = integration?.pk; // Get the actual integration instance ID
    const mappingsNote =
      resolveMappingKind(integration) === 'ingestion' ? ' Its tenant mappings will be deleted with it.' : '';

    console.log('Opening dialog for:', integrationName);
    console.log('Is ticketing integration:', isTicketingIntegration);
    console.log('Integration instance ID (pk):', integrationInstanceId);

    // Use setTimeout to ensure dialog opens after current change detection cycle
    setTimeout(() => {
      try {
        const dialog: DialogRef = this.dialogService.open({
          title: 'Delete Configuration',
          content: `Are you sure you want to delete the configuration for ${integrationName}?${mappingsNote} This action cannot be undone.`,
          actions: [
            { text: 'Cancel', primary: false },
            { text: 'Delete', primary: true, themeColor: 'error' },
          ],
          width: 450,
          height: 200,
        });

        dialog.result.subscribe((result) => {
          if (result instanceof DialogCloseResult || (result as any).text !== 'Delete') {
            return;
          }

          this.loading.set(true);
          this.cdr.markForCheck();

          if (isTicketingIntegration) {
            if (!integrationInstanceId) {
              this.loading.set(false);
              this.showNotification('No configuration found to delete', 'error');
              return;
            }
            // Ticketing: DELETE the integration detail record
            this.connectWiseService
              .deleteIntegration(integrationInstanceId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: () => {
                  this.loading.set(false);
                  this.showNotification('Configuration deleted successfully', 'success');
                  this.resetConfigForm(integrationId);
                  this.testSuccess.set(false);
                  this.loadIntegrations();
                  this.cdr.markForCheck();
                },
                error: (error: any) => {
                  this.loading.set(false);
                  console.error('Error deleting configuration:', error);
                  const errorMessage = error.error?.message || error.message || 'Failed to delete configuration';
                  this.showNotification('Error deleting configuration: ' + errorMessage, 'error');
                  this.cdr.markForCheck();
                },
              });
          } else {
            // Data ingestor: fetch the record then actually DELETE it
            this.ingestorService
              .getIngestorRecordsByOrganizationv2(orgId, integrationId)
              .pipe(
                switchMap((record) => {
                  if (!record?.id) {
                    return of(null);
                  }
                  return this.ingestorService.deleteIngestorRecord(record.id);
                }),
                takeUntilDestroyed(this.destroyRef)
              )
              .subscribe({
                next: (response) => {
                  this.loading.set(false);
                  if (!response) {
                    this.showNotification('No configuration found to delete', 'warning');
                    this.cdr.markForCheck();
                    return;
                  }
                  const deleteMessage =
                    response.status === 202
                      ? 'Delete queued — Elastic cleanup is running in the background.'
                      : 'Configuration deleted successfully';
                  this.showNotification(deleteMessage, 'success');
                  this.resetConfigForm(integrationId);
                  this.testSuccess.set(false);
                  this.loadIntegrations();
                  this.cdr.markForCheck();
                },
                error: (error: any) => {
                  this.loading.set(false);
                  console.error('Error deleting configuration:', error);
                  const errorMessage = formatIngestorApiError(error, 'Failed to delete configuration');
                  this.showNotification('Error deleting configuration: ' + errorMessage, 'error');
                  this.cdr.markForCheck();
                },
              });
          }
        });
      } catch (error) {
        console.error('Error opening dialog:', error);
        this.showNotification('Error opening confirmation dialog', 'error');
      }
    }, 0);
  }
}
